import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { Form, Button, Card, Row, Col, Badge } from 'react-bootstrap';
import { CheckCircle2, ArrowLeft, ArrowRight } from 'lucide-react';
import { useAlert } from '../../context/AlertContext';

interface Professional {
    id: number;
    name: string;
}

interface Service {
    id: number;
    name: string;
    price: string;
}

interface ProfessionalService {
    id: number;
    professional: number;
    service: number;
}

interface Props {
    onNext: () => void;
    onBack: () => void;
}

export const LinkServicesStep: React.FC<Props> = ({ onNext, onBack }) => {
    const [professionals, setProfessionals] = useState<Professional[]>([]);
    const [services, setServices] = useState<Service[]>([]);
    const [linkedServices, setLinkedServices] = useState<ProfessionalService[]>([]);
    const [fetching, setFetching] = useState(true);
    const [processingId, setProcessingId] = useState<string | null>(null);
    const { showAlert } = useAlert();

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [profsRes, servsRes, linksRes] = await Promise.all([
                api.get('/professionals/'),
                api.get('/services/'),
                api.get('/professional-services/')
            ]);
            setProfessionals(profsRes.data);
            setServices(servsRes.data);
            setLinkedServices(linksRes.data);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setFetching(false);
        }
    };

    const handleToggleService = async (profId: number, servId: number, isLinked: boolean, linkId?: number) => {
        const processKey = `${profId}-${servId}`;
        setProcessingId(processKey);
        try {
            if (isLinked && linkId) {
                // Remove link
                await api.delete(`/professional-services/${linkId}/`);
                setLinkedServices(prev => prev.filter(link => link.id !== linkId));
            } else if (!isLinked) {
                // Add link
                const selectedService = services.find(s => s.id === servId);
                const response = await api.post('/professional-services/', {
                    professional: profId,
                    service: servId,
                    custom_price: selectedService?.price || "0.00"
                });
                setLinkedServices(prev => [...prev, response.data]);
            }
        } catch (error) {
            console.error('Error toggling service link:', error);
            showAlert('Erro ao vincular/desvincular serviço. Tente novamente.', { type: 'error' });
        } finally {
            setProcessingId(null);
        }
    };

    if (fetching) {
        return <div className="text-center p-5">Carregando...</div>;
    }

    // Check if every professional has at least one linked service
    const isReadyToProceed = professionals.length > 0 && professionals.every(prof =>
        linkedServices.some(link => link.professional === prof.id)
    );

    return (
        <div>
            <div className="step-header">
                <h3>Vincular Serviços</h3>
                <p className="text-muted">Selecione quais serviços cada profissional realiza. Cada profissional precisa ter pelo menos um serviço vinculado.</p>
            </div>

            <div className="link-services-container border rounded bg-white p-3 p-md-4 mb-4">
                {professionals.map(prof => {
                    const profLinks = linkedServices.filter(l => l.professional === prof.id);
                    const hasLinks = profLinks.length > 0;

                    return (
                        <Card key={prof.id} className={`mb-4 border ${hasLinks ? 'border-success-subtle' : 'border-warning-subtle'}`}>
                            <Card.Header className="bg-light d-flex justify-content-between align-items-center py-3">
                                <h5 className="mb-0 fw-bold">{prof.name}</h5>
                                {hasLinks ? (
                                    <Badge bg="success" pill>{profLinks.length} serviço(s)</Badge>
                                ) : (
                                    <Badge bg="warning" text="dark" pill>Nenhum serviço</Badge>
                                )}
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    {services.map(serv => {
                                        const link = profLinks.find(l => l.service === serv.id);
                                        const isLinked = !!link;
                                        const isProcessing = processingId === `${prof.id}-${serv.id}`;

                                        return (
                                            <Col xs={12} sm={6} lg={4} key={serv.id} className="mb-3">
                                                <div className={`p-3 rounded border ${isLinked ? 'bg-primary-subtle border-primary' : 'bg-white'}`}>
                                                    <Form.Check
                                                        type="checkbox"
                                                        id={`check-${prof.id}-${serv.id}`}
                                                        label={<span className="ms-2">{serv.name}</span>}
                                                        checked={isLinked}
                                                        onChange={() => handleToggleService(prof.id, serv.id, isLinked, link?.id)}
                                                        disabled={isProcessing}
                                                        className="user-select-none custom-checkbox-bold m-0 d-flex align-items-center"
                                                    />
                                                </div>
                                            </Col>
                                        );
                                    })}
                                </Row>
                            </Card.Body>
                        </Card>
                    );
                })}
            </div>

            <div className="step-navigation">
                {isReadyToProceed ? (
                    <div className="step-navigation-status text-success">
                        <CheckCircle2 size={20} /> Pronto para prosseguir
                    </div>
                ) : (
                    <div className="step-navigation-status text-muted">
                        Vincule pelo menos 1 serviço a cada profissional
                    </div>
                )}
                <div className="step-navigation-buttons">
                    <Button variant="outline-secondary" onClick={onBack} className="btn-step-back">
                        <ArrowLeft size={18} /> Voltar
                    </Button>
                    <Button className="btn-primary-premium btn-step-next" onClick={onNext} disabled={!isReadyToProceed}>
                        Próximo Passo <ArrowRight size={18} />
                    </Button>
                </div>
            </div>
        </div>
    );
};
